import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-logout',
  template: `
    <div class="logout-container">
      <div class="scryber-icon-row">
        <img src="/scryber-logo-lime.png?v=4" alt="Scryber" />
      </div>
      <div class="logout-card">
        <div class="logout-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16,17 21,12 16,7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </div>
        
        <div class="logout-content">
          <h1>You've been logged out</h1>
          <p>Thank you for using Scryber. Your session has been securely ended.</p>
          
          <div class="logout-actions">
            <button (click)="loginAgain()" class="login-again-button">
              Sign In Again
            </button>
          </div>
        </div>
        
        <div class="logout-footer">
          <p>Need help? <a href="#" class="help-link">Contact Support</a></p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .logout-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 100vh;
      background: linear-gradient(160deg, var(--scryber-rail-from) 0%, var(--scryber-rail-to) 55%, #022c22 100%);
      padding: 0 0 20px;
    }
    
    .logout-card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
      padding: 48px;
      width: calc(100% - 40px);
      max-width: 450px;
      margin: auto;
      text-align: center;
      animation: slideUp 0.6s ease-out;
    }
    
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .logout-icon {
      width: 80px;
      height: 80px;
      margin: 0 auto 24px;
      background: linear-gradient(135deg, var(--scryber-green-600) 0%, var(--scryber-rail-to) 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }
    
    .logout-icon svg {
      width: 40px;
      height: 40px;
    }
    
    .logout-content h1 {
      color: #2d3748;
      font-size: 32px;
      font-weight: 700;
      margin: 0 0 16px 0;
    }
    
    .logout-content p {
      color: #718096;
      font-size: 18px;
      margin: 0 0 32px 0;
      line-height: 1.6;
    }
    
    .logout-actions {
      display: flex;
      justify-content: center;
    }
    
    .login-again-button {
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 140px;
      background: linear-gradient(135deg, var(--scryber-green-600) 0%, var(--scryber-rail-to) 100%);
      color: white;
    }
    
    .login-again-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
    
    .logout-footer {
      margin-top: 32px;
      padding-top: 24px;
      border-top: 1px solid #e5e7eb;
    }
    
    .logout-footer p {
      color: #9ca3af;
      font-size: 14px;
      margin: 0;
    }
    
    .help-link {
      color: var(--scryber-green-600);
      text-decoration: none;
      font-weight: 500;
    }
    
    .help-link:hover {
      text-decoration: underline;
    }
    
    @media (max-width: 480px) {
      .logout-card {
        padding: 32px 24px;
        margin: 0 16px;
      }
      
      .logout-content h1 {
        font-size: 28px;
      }
      
      .login-again-button {
        width: 100%;
        max-width: 200px;
      }
    }
  `]
})
export class LogoutComponent implements OnInit {
  
  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}
  
  ngOnInit() {
    // Clear the JWT token from localStorage
    localStorage.removeItem('jwt_token');
    
    // Clear all cookies (including token cookie if present)
    this.clearAllCookies();
    
    // Also clear sessionStorage for good measure
    sessionStorage.clear();
    
    // Try to call backend logout endpoint to invalidate server-side session
    // This is best-effort - if it fails, we still clear client-side tokens
    this.callBackendLogout().catch(() => {
      // Ignore errors - client-side cleanup is more important
      console.log('Backend logout call failed, but client-side tokens cleared');
    });
  }
  
  /**
   * Clear all cookies by setting them to expire in the past
   * This handles cookies set by the backend (OAuth2 flow)
   * Note: HttpOnly cookies cannot be cleared by JavaScript, but we try anyway
   */
  private clearAllCookies(): void {
    const hostname = window.location.hostname;
    
    // List of common cookie names that might be set
    const commonCookieNames = ['token', 'JSESSIONID', 'SESSION', 'SESSIONID', 'auth', 'authentication'];
    
    // Get all cookies from current domain
    const cookies = document.cookie.split(';');
    
    // Clear each cookie found
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name) {
        this.clearCookie(name, hostname);
      }
    });
    
    // Also try to clear common cookie names (in case they're HttpOnly and not in document.cookie)
    commonCookieNames.forEach(name => {
      this.clearCookie(name, hostname);
    });
  }
  
  /**
   * Clear a specific cookie with multiple domain/path combinations
   */
  private clearCookie(name: string, hostname: string): void {
    const pastDate = 'Thu, 01 Jan 1970 00:00:00 GMT';
    
    // Clear for current path and root path
    const paths = ['/', window.location.pathname];
    
    paths.forEach(path => {
      // Clear for current domain
      document.cookie = `${name}=;expires=${pastDate};path=${path};`;
      
      // Clear without domain (current domain only)
      document.cookie = `${name}=;expires=${pastDate};path=${path};`;
      
      // Clear with domain if on subdomain
      if (hostname.indexOf('.') > 0) {
        const domain = '.' + hostname.split('.').slice(-2).join('.');
        document.cookie = `${name}=;expires=${pastDate};path=${path};domain=${domain};`;
      }
      
      // Clear for scryber.ai domain
      if (hostname !== 'scryber.ai' && !hostname.endsWith('.scryber.ai')) {
        document.cookie = `${name}=;expires=${pastDate};path=${path};domain=.scryber.ai;`;
      }
      
      // Clear for midoc.io domain (backend domain)
      if (!hostname.endsWith('.midoc.io')) {
        document.cookie = `${name}=;expires=${pastDate};path=${path};domain=.midoc.io;`;
      }
    });
    
    // Also try with Secure and SameSite attributes
    document.cookie = `${name}=;expires=${pastDate};path=/;Secure;SameSite=None;`;
    document.cookie = `${name}=;expires=${pastDate};path=/;Secure;SameSite=Lax;`;
    document.cookie = `${name}=;expires=${pastDate};path=/;Secure;SameSite=Strict;`;
  }
  
  /**
   * Call backend logout endpoint to invalidate server-side session
   */
  private async callBackendLogout(): Promise<void> {
    // Only run in browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      // Determine backend URL based on environment (same logic as login component)
      let backendUrl: string;
      
      if (environment.environment === 'local') {
        // Local development: backend is on localhost:8080
        backendUrl = 'http://localhost:8080/logout';
      } else {
        // Development/Staging/Production: use backend URL from environment configuration
        // Backend URL is configured in environment files (backendUrl property)
        const envBackendUrl = (environment as any).backendUrl || 'https://us-east-1.dev.midoc.io';
        backendUrl = `${envBackendUrl}/logout`;
      }
      
      const response = await fetch(backendUrl, {
        method: 'POST',
        credentials: 'include', // Include cookies so backend can clear them
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log('Backend logout successful');
      }
    } catch (error) {
      // Ignore errors - client-side cleanup is sufficient
      console.log('Backend logout call failed (this is OK):', error);
    }
  }
  
  loginAgain() {
    this.router.navigate(['/login']);
  }
}
